import type { ReactNode } from 'react';

import { colors } from '@packages/core/styles';
import { Typography } from '@packages/ui/shared';
import { Children } from 'react';
import { createUseStyles } from 'react-jss';

import { ssm5TextStyles } from '../ssm5.styles';

/** Props for {@link Ssm5Card}. */
export type Ssm5CardProps = {
    /** The control at the end of the header, if any. */
    action?: ReactNode;
    /**
     * The rows of the card, each set off from the one above by a divider. An empty child, such as a
     * condition that does not hold, is no row.
     */
    children: ReactNode;
    /** The icon before the title, if any. */
    icon?: ReactNode;
    /** The title of the card; a card without a title, an icon or an action has no header. */
    title?: string;
};

/** The divider under the header and under each row but the last, which the border of the card closes. */
const DIVIDER = `inset 0 -1px 0 ${colors.grayThree}`;

// The design draws the border of the card and its dividers inside the blocks, where they take no room.
// They are inset shadows here, so the card, its header and its rows keep the sizes of the design.
const useStyles = createUseStyles({
    card: {
        backgroundColor: colors.white,
        borderRadius: 4,
        boxShadow: `inset 0 0 0 1px ${colors.grayThree}`,
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        alignItems: 'center',
        boxShadow: DIVIDER,
        display: 'flex',
        gap: 8,
        minHeight: 56,
        padding: '8px 8px 8px 16px',
    },
    // The title takes the room the icon and the action leave, so the action sits at the end of the header.
    title: {
        flex: 1,
        minWidth: 0,
    },
    row: {
        '&:last-child': {
            boxShadow: 'none',
        },
        boxShadow: DIVIDER,
        padding: '12px 16px',
    },
});

/**
 * A white SSM5 card: rows split by dividers, under a titled header when it has one.
 *
 * @param props - {@link Ssm5CardProps}
 * @returns the card
 */
export function Ssm5Card({ action, children, icon, title }: Ssm5CardProps) {
    const classes = useStyles();
    const hasHeader = title !== undefined || icon !== undefined || action !== undefined;

    return (
        <div className={classes.card}>
            {hasHeader && (
                <div className={classes.header}>
                    {icon}
                    <div className={classes.title}>
                        <Typography color="blueOne" style={ssm5TextStyles.bodySemiboldTight} variant="h4">
                            {title}
                        </Typography>
                    </div>
                    {action}
                </div>
            )}
            {Children.map(children, (row) => (row === null ? null : <div className={classes.row}>{row}</div>))}
        </div>
    );
}
